import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VerifiedIcon from "@mui/icons-material/Verified";
import CloseIcon from "@mui/icons-material/Close";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { DATABASE, FUNCTIONS } from "../../../Config/firebase";
import { maskAddress, WALLET_TYPES } from "../../../Utils/cryptoWalletUtils";
import { buildReconciliation, CATEGORIES, BYBIT_CHAIN_TO_NETWORK, ackDocIdForKey } from "../../../Utils/walletReconciliation";

const PANEL_SX = {
  p: 2,
  borderRadius: 2,
  bgcolor: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const SEVERITY_COLOR = {
  high: "#ff8a80",
  medium: "#ffb74d",
  info: "#7eb8ff",
  ok: "#2af598",
};

const formatTs = (value) => {
  if (!value) return "never";
  const m = value?.toDate ? moment(value.toDate()) : value?.seconds ? moment(value.seconds * 1000) : moment(value);
  return m?.isValid() ? m.format("DD MMM YYYY, HH:mm") : "never";
};

const WalletReconciliation = () => {
  const navigate = useNavigate();

  const [registeredWallets, setRegisteredWallets] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [exchangeRows, setExchangeRows] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncedBy, setSyncedBy] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [relayConfigured, setRelayConfigured] = useState(true);
  const [revealed, setRevealed] = useState({});
  const [severityFilter, setSeverityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [acknowledgements, setAcknowledgements] = useState({});
  const [ackGroup, setAckGroup] = useState(null);
  const [ackSelected, setAckSelected] = useState([]);
  const [ackNote, setAckNote] = useState("");
  const [ackSaving, setAckSaving] = useState(false);

  // Load registered wallets (+client names) and client confirmations.
  const loadLocalData = useCallback(async () => {
    setLoading(true);
    try {
      const usersSnap = await getDocs(query(collection(DATABASE, "users"), where("role", "==", 1)));
      const users = usersSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      const nameByUid = {};
      users.forEach((u) => { nameByUid[u.doc_id || u.uid] = u.full_name || u.email || (u.doc_id || u.uid); });

      const rows = [];
      await Promise.all(
        users.map(async (user) => {
          const userId = user.doc_id || user.uid;
          try {
            const walletsSnap = await getDocs(collection(DATABASE, "users", userId, "crypto_wallets"));
            walletsSnap.docs.forEach((wDoc) => {
              const w = wDoc.data();
              rows.push({
                user_id: userId,
                client_name: user.full_name || "—",
                client_email: user.email || "",
                asset: w.asset || "USDT",
                network: w.network || "POLYGON",
                address: w.address || "",
                label: w.label || "",
                wallet_type: w.wallet_type || WALLET_TYPES.WITHDRAWAL,
                is_active: w.is_active !== false,
                is_default: !!w.is_default,
              });
            });
          } catch (err) {
            console.warn(`Failed to load wallets for ${userId}`, err);
          }
        })
      );
      setRegisteredWallets(rows);

      const confSnap = await getDocs(collectionGroup(DATABASE, "wallet_confirmations"));
      const confs = confSnap.docs.map((d) => {
        const c = d.data();
        const userId = d.ref.parent.parent?.id || "";
        return {
          userId,
          client_name: nameByUid[userId] || userId,
          asset: c.asset || "USDT",
          network: c.network || "POLYGON",
          address: c.address || "",
        };
      });
      setConfirmations(confs);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load wallets/confirmations");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the last persisted Bybit snapshot.
  const loadSnapshot = useCallback(async () => {
    try {
      const snap = await getDoc(doc(DATABASE, "exchange_whitelists", "bybit"));
      if (snap.exists()) {
        const data = snap.data();
        setExchangeRows(Array.isArray(data.rows) ? data.rows : []);
        setSyncedAt(data.synced_at || null);
        setSyncedBy(data.synced_by || "");
      }
    } catch (err) {
      console.warn("Failed to load exchange snapshot", err?.message);
    }
  }, []);

  // Load admin acknowledgements ("verified OK" with a reason) for flagged groups.
  const loadAcks = useCallback(async () => {
    try {
      const snap = await getDocs(collection(DATABASE, "exchange_reconciliation_acks"));
      const map = {};
      snap.docs.forEach((d) => {
        const a = d.data();
        if (a?.key) {
          map[a.key] = {
            categories: Array.isArray(a.categories) ? a.categories : [],
            note: a.note || "",
            by: a.acknowledged_by || "",
            at: a.acknowledged_at || null,
          };
        }
      });
      setAcknowledgements(map);
    } catch (err) {
      console.warn("Failed to load acknowledgements", err?.message);
    }
  }, []);

  useEffect(() => {
    loadLocalData();
    loadSnapshot();
    loadAcks();
  }, [loadLocalData, loadSnapshot, loadAcks]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const fn = httpsCallable(FUNCTIONS, "getBybitWithdrawalWhitelist");
      const res = await fn();
      const data = res.data || {};
      if (data.configured === false) {
        setRelayConfigured(false);
        toast.warn("Bybit relay is not configured");
        return;
      }
      const rows = Array.isArray(data.rows) ? data.rows : [];
      setExchangeRows(rows);
      const adminEmail = localStorage.getItem("email") || "admin";
      await setDoc(
        doc(DATABASE, "exchange_whitelists", "bybit"),
        { exchange: "bybit", rows, synced_at: serverTimestamp(), synced_by: adminEmail },
        { merge: true }
      );
      setSyncedAt(new Date());
      setSyncedBy(adminEmail);
      toast.success(`Synced ${rows.length} address${rows.length !== 1 ? "es" : ""} from Bybit`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to sync from Bybit");
    } finally {
      setSyncing(false);
    }
  };

  const { groups, summary } = useMemo(
    () => buildReconciliation({ registeredWallets, confirmations, exchangeRows, acknowledgements }),
    [registeredWallets, confirmations, exchangeRows, acknowledgements]
  );

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (severityFilter && g.severity !== severityFilter) return false;
      if (categoryFilter && !(g.allCategories || g.categories).includes(categoryFilter)) return false;
      if (q) {
        const inAddr = (g.address || "").toLowerCase().includes(q);
        const inClient = (g.clientNames || []).some((n) => (n || "").toLowerCase().includes(q));
        if (!inAddr && !inClient) return false;
      }
      return true;
    });
  }, [groups, severityFilter, categoryFilter, search]);

  const toggleReveal = (key) => setRevealed((p) => ({ ...p, [key]: !p[key] }));
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.info("Address copied"); };

  const openAckDialog = (g) => {
    setAckGroup(g);
    const existing = acknowledgements[g.key];
    // Preselect already-acknowledged categories, otherwise all currently flagged.
    setAckSelected(existing ? existing.categories : g.allCategories || []);
    setAckNote(existing?.note || "");
  };
  const closeAckDialog = () => { setAckGroup(null); setAckSelected([]); setAckNote(""); };
  const toggleAckCategory = (id) =>
    setAckSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const saveAck = async () => {
    if (!ackGroup) return;
    setAckSaving(true);
    try {
      const id = ackDocIdForKey(ackGroup.key);
      const ref = doc(DATABASE, "exchange_reconciliation_acks", id);
      const adminEmail = localStorage.getItem("email") || "admin";
      if (ackSelected.length === 0) {
        await deleteDoc(ref);
        setAcknowledgements((p) => { const n = { ...p }; delete n[ackGroup.key]; return n; });
        toast.info("Verification removed");
      } else {
        const payload = {
          key: ackGroup.key,
          address: ackGroup.address,
          chain: ackGroup.bybitChain,
          categories: ackSelected,
          note: ackNote.trim(),
          acknowledged_by: adminEmail,
          acknowledged_at: serverTimestamp(),
        };
        await setDoc(ref, payload);
        setAcknowledgements((p) => ({
          ...p,
          [ackGroup.key]: { categories: ackSelected, note: ackNote.trim(), by: adminEmail, at: new Date() },
        }));
        toast.success("Marked as verified");
      }
      closeAckDialog();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to save verification");
    } finally {
      setAckSaving(false);
    }
  };

  const cards = [
    { label: "Registered (withdrawal)", value: summary.registered, color: "#fff" },
    { label: "On exchange (Bybit)", value: summary.inExchange, color: "#fff" },
    { label: "Matched", value: summary.matched, color: "#2af598" },
    { label: "High severity", value: summary.high, color: summary.high > 0 ? "#ff8a80" : "#2af598" },
    { label: "Medium severity", value: summary.medium, color: summary.medium > 0 ? "#ffb74d" : "#2af598" },
    { label: "Shared (expected)", value: summary.shared, color: "#7eb8ff" },
    { label: "Verified OK", value: summary.verified || 0, color: "#2af598" },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            Wallet Reconciliation
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", mt: 0.75, maxWidth: 760, lineHeight: 1.5 }}>
            Compares what the client sent (Wallet Check), what we registered (Wallet Management) and what is
            whitelisted on the exchange (Bybit, live). Grouped by address — shared wallets and multi-account
            holders are expected and shown as info, not errors.
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 0.5 }}>
            Last Bybit sync: {formatTs(syncedAt)}{syncedBy ? ` · ${syncedBy}` : ""}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} sx={{ color: "#000" }} /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
          sx={{
            background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
            color: "#000", fontWeight: 700, flexShrink: 0,
            "&:hover": { background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)" },
            "&:disabled": { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)" },
          }}
        >
          {syncing ? "Syncing…" : "Sync from Bybit"}
        </Button>
      </Box>

      {!relayConfigured && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          Bybit relay is not configured in this environment — the exchange column will be empty.
        </Alert>
      )}

      {/* Summary cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
        {cards.map((s) => (
          <Box key={s.label} sx={{ flex: "1 1 150px", minWidth: 140, p: 1.75, borderRadius: 2, bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: s.color, lineHeight: 1.1 }}>
              {loading ? "—" : s.value}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block", mt: 0.25, lineHeight: 1.3 }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, ...PANEL_SX }}>
        <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: 140 }}>
          <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>Severity</InputLabel>
          <Select label="Severity" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} sx={{ color: "#fff" }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="info">Info / shared</MenuItem>
            <MenuItem value="ok">OK</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: "1 1 220px", minWidth: 200 }}>
          <InputLabel sx={{ color: "rgba(255,255,255,0.7)" }}>Issue type</InputLabel>
          <Select label="Issue type" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} sx={{ color: "#fff" }}>
            <MenuItem value="">All</MenuItem>
            {Object.values(CATEGORIES).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: "1 1 220px", minWidth: 200, display: "flex", alignItems: "center" }}>
          <input
            placeholder="Search address or client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", height: 40, borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.07)", color: "#fff", padding: "0 12px", outline: "none" }}
          />
        </Box>
      </Box>

      {/* Mobile note */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
        <Alert severity="info" variant="outlined">
          The full reconciliation table is available on desktop. The summary above works on mobile.
        </Alert>
      </Box>

      {/* Table — desktop only */}
      <Box sx={{ display: { xs: "none", md: "block" }, ...PANEL_SX }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading…</Typography>
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            {groups.length === 0 ? "No wallets to reconcile yet. Click \"Sync from Bybit\" to pull the whitelist." : "No groups match the current filters."}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 620, borderRadius: 1.5, border: "1px solid rgba(255,255,255,0.1)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {["Address", "Asset / Network", "Client(s)", "System", "Client sent", "Bybit", "Issues", "Verify"].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: "#16201c", color: "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.12)", whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.map((g) => {
                  const isRevealed = revealed[g.key];
                  const network = BYBIT_CHAIN_TO_NETWORK[g.bybitChain] || g.network || g.bybitChain;
                  const assets = [...new Set([
                    ...g.systemWallets.map((w) => w.asset),
                    g.exchange?.coin,
                  ].filter(Boolean))].join(", ");
                  const sysActive = g.systemWallets.filter((w) => w.is_active).length;
                  return (
                    <TableRow key={g.key} hover>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "#fff", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {isRevealed ? g.address : maskAddress(g.address, network)}
                          </Typography>
                          <IconButton size="small" onClick={() => toggleReveal(g.key)} sx={{ color: "rgba(255,255,255,0.6)", p: 0.25 }}>
                            {isRevealed ? <VisibilityOffIcon sx={{ fontSize: 15 }} /> : <VisibilityIcon sx={{ fontSize: 15 }} />}
                          </IconButton>
                          <IconButton size="small" onClick={() => copyToClipboard(g.address)} sx={{ color: "rgba(255,255,255,0.6)", p: 0.25 }}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                        {`${assets || "—"} · ${network}`}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 240 }}>
                          {g.clientNames.length === 0 ? (
                            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>—</Typography>
                          ) : g.clientNames.map((name, idx) => {
                            const uid = g.systemWallets.find((w) => (w.client_name || w.client_email) === name)?.user_id;
                            return (
                              <Chip
                                key={`${g.key}-${idx}`}
                                size="small"
                                label={name}
                                onClick={uid ? () => navigate(`/admin/wallet-management?client=${uid}`) : undefined}
                                sx={{ height: 22, fontSize: 11, color: "#fff", bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", cursor: uid ? "pointer" : "default", "&:hover": uid ? { bgcolor: "rgba(255,255,255,0.15)" } : {} }}
                              />
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                        {g.systemWallets.length === 0 ? (
                          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>—</Typography>
                        ) : (
                          <Typography sx={{ color: "#fff", fontSize: 13 }}>{sysActive} active</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        {g.confirmations.length > 0
                          ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2af598" }} />
                          : <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        {g.exchange ? (
                          <Chip
                            size="small"
                            label={g.exchange.verified === 1 ? "Verified" : "Listed"}
                            sx={{ height: 20, fontSize: 10, fontWeight: 700, color: "#2af598", bgcolor: "rgba(42,245,152,0.12)", border: "1px solid rgba(42,245,152,0.35)" }}
                          />
                        ) : (
                          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 280 }}>
                          {g.categories.length === 0 && g.acknowledgedCategories.length === 0 && (
                            <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />} label="OK" sx={{ height: 20, fontSize: 10, fontWeight: 700, color: "#2af598", bgcolor: "rgba(42,245,152,0.1)", border: "1px solid rgba(42,245,152,0.3)", "& .MuiChip-icon": { color: "#2af598" } }} />
                          )}
                          {g.categories.map((id) => {
                            const cat = CATEGORIES[id];
                            const color = SEVERITY_COLOR[cat?.severity] || "#fff";
                            return (
                              <Tooltip key={id} title={cat?.hint || ""}>
                                <Chip
                                  size="small"
                                  label={cat?.label || id}
                                  icon={cat?.severity === "info" ? undefined : <WarningAmberIcon sx={{ fontSize: 13 }} />}
                                  sx={{ height: 20, fontSize: 10, fontWeight: 700, color, bgcolor: "transparent", border: `1px solid ${color}66`, "& .MuiChip-icon": { color } }}
                                />
                              </Tooltip>
                            );
                          })}
                          {g.acknowledgedCategories.map((id) => {
                            const cat = CATEGORIES[id];
                            const tip = `Verified OK${g.ack?.note ? `: ${g.ack.note}` : ""}${g.ack?.by ? ` — ${g.ack.by}` : ""}`;
                            return (
                              <Tooltip key={`ack-${id}`} title={tip}>
                                <Chip
                                  size="small"
                                  icon={<VerifiedIcon sx={{ fontSize: 13 }} />}
                                  label={cat?.label || id}
                                  sx={{ height: 20, fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.5)", bgcolor: "rgba(42,245,152,0.06)", border: "1px solid rgba(42,245,152,0.25)", textDecoration: "line-through", "& .MuiChip-icon": { color: "#2af598" } }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                        {g.allCategories && g.allCategories.length > 0 ? (
                          g.verified ? (
                            <Tooltip title={g.ack?.note ? `Verified: ${g.ack.note}` : "Verified OK"}>
                              <Chip
                                size="small"
                                icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                                label="Verified"
                                onClick={() => openAckDialog(g)}
                                sx={{ height: 22, fontSize: 10, fontWeight: 700, color: "#2af598", bgcolor: "rgba(42,245,152,0.12)", border: "1px solid rgba(42,245,152,0.35)", cursor: "pointer", "& .MuiChip-icon": { color: "#2af598" } }}
                              />
                            </Tooltip>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openAckDialog(g)}
                              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11, color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.25)", textTransform: "none", "&:hover": { borderColor: "#2af598", color: "#2af598" } }}
                            >
                              Mark OK
                            </Button>
                          )
                        ) : (
                          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Verify / acknowledge dialog */}
      <Dialog
        open={!!ackGroup}
        onClose={closeAckDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#16201c", color: "#fff", borderRadius: 2, border: "1px solid rgba(255,255,255,0.1)" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VerifiedIcon sx={{ color: "#2af598" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Mark as verified</Typography>
          </Box>
          <IconButton onClick={closeAckDialog} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.1)" }}>
          {ackGroup && (
            <>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", display: "block" }}>
                Address ({BYBIT_CHAIN_TO_NETWORK[ackGroup.bybitChain] || ackGroup.bybitChain})
              </Typography>
              <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "#fff", wordBreak: "break-all", mb: 2 }}>
                {ackGroup.address}
              </Typography>

              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 1 }}>
                Select which findings are expected and OK:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
                {(ackGroup.allCategories || []).map((id) => {
                  const cat = CATEGORIES[id];
                  return (
                    <FormControlLabel
                      key={id}
                      control={
                        <Checkbox
                          checked={ackSelected.includes(id)}
                          onChange={() => toggleAckCategory(id)}
                          sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#2af598" } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: 13, color: "#fff" }}>{cat?.label || id}</Typography>
                          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{cat?.hint}</Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 0.5 }}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1.5 }} />

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Reason / explanation"
                placeholder="e.g. Same EVM address receives both USDC and USDT — normal for this wallet."
                value={ackNote}
                onChange={(e) => setAckNote(e.target.value)}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.6)" } }}
                InputProps={{ sx: { color: "#fff", "& fieldset": { borderColor: "rgba(255,255,255,0.2)" } } }}
              />
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", display: "block", mt: 1 }}>
                This is saved as an audit note. Unchecking everything removes the verification.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeAckDialog} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={saveAck}
            disabled={ackSaving}
            variant="contained"
            startIcon={ackSaving ? <CircularProgress size={14} sx={{ color: "#000" }} /> : <VerifiedIcon />}
            sx={{ background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)", color: "#000", fontWeight: 700, textTransform: "none", "&:hover": { background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)" } }}
          >
            {ackSelected.length === 0 ? "Remove verification" : "Save verification"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletReconciliation;
